import { delay, firstValueFrom, of, race } from 'rxjs';
import type { DefaultMeetingSession } from 'amazon-chime-sdk-js';

import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { callIdSelector } from 'features/call/call-base/store/selectors';
import { deafPhoneNumberSelector } from 'features/call/call-deaf/store';
import { setPrimaryHearingDirection } from 'features/call/call-hearing/store';
import { ParticipantsStatusEventBus } from 'features/call/call-status/services';
import {
  adminContactSelector,
  isMonitorHostIpExistsSelector,
} from 'features/monitoring/store/selector';
import { useSourceMediaTracks } from 'features/source-media-tracks/hooks';
import { userNameSelector } from 'features/user/store';
import { createAgentParticipant } from 'features/voice-meeting/helpers';
import {
  useVoiceMeetingConnection,
  useVoiceMeetingContext,
  useVoiceMeetingSession,
} from 'features/voice-meeting/hooks';
import type { CreateVoiceMeetingParams } from 'features/voice-meeting/interfaces';
import { VoiceSessionStatus } from 'features/voice-session/enums';
import type { VoiceSessionResponse } from 'features/voice-session/interfaces';
import { setVoiceSessionThunk } from 'features/voice-session/store';
import { isVrsModeSelector } from 'features/multi-mode/store';

export const useVoiceMeetingSessionConnect = () => {
  const dispatch = useAppDispatch();
  const {
    createMeetingSessionFromState,
    bindAudio,
    addParticipant,
    createVoiceSession,
  } = useVoiceMeetingConnection();

  const { audio, service } = useVoiceMeetingContext();
  const { startMeeting, addMonitorParticipant } = useVoiceMeetingSession();
  const isCallMonitoring = useAppSelector(isMonitorHostIpExistsSelector);
  const adminContact = useAppSelector(adminContactSelector);
  const agentName = useAppSelector(userNameSelector);
  const currentCallId = useAppSelector(callIdSelector);
  const deafPhoneNumber = useAppSelector(deafPhoneNumberSelector);
  const isVrs = useAppSelector(isVrsModeSelector);

  // DO NOT REMOVE THIS, AS IT WILL CAUSE CONNECTS FOR BILLING TO POTENTIALLY FAIL.
  // ONLY REMOVE THIS ONCE WE ARE NOT USING THE WEBSOCKET FOR BILLING CONNECTS.
  const awaitForWSConnect = async () => {
    const timeout$ = of('Timeout').pipe(delay(60000));
    return await firstValueFrom(
      race(ParticipantsStatusEventBus.hearing.$connectedToWebsocket, timeout$)
    );
  };
  const { setHearingVoiceMeetingSession } = useSourceMediaTracks();

  const createSession = async ({
    agentUserName,
    ...rest
  }: CreateVoiceMeetingParams) => {
    const voiceSessionState = await createVoiceSession({
      agentUserName,
      ...rest,
    });

    return {
      session: createMeetingSessionFromState(voiceSessionState, agentUserName),
      voiceSessionState,
    };
  };

  const setActiveSession = async (
    meetingSession: DefaultMeetingSession,
    voiceSessionState: VoiceSessionResponse,
    audioElement: HTMLAudioElement,
    shouldAwaitWsConnect = false
  ) => {
    await setHearingVoiceMeetingSession(meetingSession);
    await bindAudio(meetingSession, audioElement);

    service.setMeetingSession(meetingSession);
    service.setVoiceSession(voiceSessionState);

    const voiceSessionId = service.getSessionId();

    if (isCallMonitoring) {
      await addMonitorParticipant(adminContact, voiceSessionId);
    }

    dispatch(
      setVoiceSessionThunk({
        voiceSessionState: {
          sessionId: voiceSessionId,
          status: VoiceSessionStatus.ACTIVE,
          isLoading: false,
        },
      })
    );

    service.setupListeners();

    // We only need to wait for the WS connection on outbound hearing calls.
    if (isVrs && shouldAwaitWsConnect) {
      await awaitForWSConnect();
      console.log(
        'DEBUG: Step 1: WS for hearing session Connected, Proceeding with startMeeting'
      );
    }

    await startMeeting();
  };

  const connectUserToExistingSession = async (
    sessionId: string,
    agentId: string,
    audioElement: HTMLAudioElement | null
  ) => {
    if (!audioElement) {
      throw new Error('DEBUG: audioElement not exists');
    }
    const voiceSessionState = await addParticipant({
      participant: createAgentParticipant(agentId),
      sessionId,
    });

    const meetingSession = createMeetingSessionFromState(
      voiceSessionState,
      agentId
    );

    await setActiveSession(meetingSession, voiceSessionState, audioElement);
  };

  const startNewSession = async ({
    agentUserName,
    hearingPhoneNumber,
    ...rest
  }: CreateVoiceMeetingParams) => {
    if (!audio) {
      throw new Error('DEBUG: audioElement not exists');
    }

    const { session, voiceSessionState } = await createSession({
      agentUserName,
      hearingPhoneNumber,
      ...rest,
    });

    await setActiveSession(session, voiceSessionState, audio, true);

    const hearingUser = service.getHearingByPhoneNumber(hearingPhoneNumber);

    if (hearingUser?.direction) {
      dispatch(setPrimaryHearingDirection(hearingUser?.direction));
    }

    if (hearingUser) {
      ParticipantsStatusEventBus.hearing.$connectingId.next({
        id: hearingUser.id,
        phoneNumber: hearingPhoneNumber,
      });
    }
  };

  const startNewVoiceSessionOnlyAgent = async () => {
    if (!audio) {
      throw new Error('DEBUG: audioElement not exists');
    }

    const { session, voiceSessionState } = await createSession({
      agentUserName: agentName,
      callId: currentCallId,
      deafPhoneNumber,
      hearingPhoneNumber: '',
    });

    await setActiveSession(session, voiceSessionState, audio);
  };

  return {
    connectUserToExistingSession,
    startNewSession,
    startNewVoiceSessionOnlyAgent,
  };
};
